import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle, ArrowRight, Check, CircleSlash, Link2, MessageSquareText, NotebookPen, Pencil,
  RotateCcw, Trash2, Trophy, X,
} from "lucide-react";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { SearchableSelect } from "@/components/ui/searchable-select";
import Loader from "@/components/ui/loader";
import { useToast } from "@/components/ui/toast";
import { stageLabel } from "@/lib/dealStages";
import { type DealBuyer, type DealCard, dealTarget, fmtExact } from "@/lib/dealCrm";
import { cn } from "@/lib/utils";

/** One entry in a deal's history — mirrors DealNote in
 *  app/schemas/deal_pipeline.py. */
export interface DealNote {
  id: string;
  kind: "note" | "stage_change" | "outcome" | string;
  body: string | null;
  from_stage: string | null;
  to_stage: string | null;
  author_name: string | null;
  audience: "staff" | "buyer" | string;
  is_mine: boolean;
  edited_at: string | null;
  created_at: string;
}

interface Props {
  card: DealCard | null;
  /** Deal-desk features: details, outcome, link buyer, remove, staff notes. */
  isStaff: boolean;
  /** Buyers with an active interest in this listing — the "Link buyer" choices
   *  on a listing-only deal. */
  buyerCandidates: DealBuyer[];
  onClose: () => void;
  /** A write changed this card; the board swaps it in. */
  onCardChanged: (card: DealCard) => void;
  /** A write changed the board's shape (first write on a virtual deal, a
   *  merge, a removal) — the board reloads in place. */
  onBoardChanged: () => void;
}

function formatWhen(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })} · ${d.toLocaleTimeString(
    undefined,
    { hour: "2-digit", minute: "2-digit" },
  )}`;
}

const OUTCOME_TEXT: Record<string, string> = { won: "Closed — won", lost: "Closed — lost", reopened: "Reopened" };

interface DetailsDraft {
  offer_amount: string;
  target_close_date: string;
  next_step: string;
  next_step_due: string;
}

const draftFrom = (c: DealCard): DetailsDraft => ({
  offer_amount: c.offer_amount != null ? String(c.offer_amount) : "",
  target_close_date: c.target_close_date ?? "",
  next_step: c.next_step ?? "",
  next_step_due: c.next_step_due ?? "",
});

/**
 * Everything about one deal. For staff: its working details (offer, target
 * close, next step), its outcome, linking a buyer to a listing-only deal, and
 * the desk's internal history. For a buyer: their own private notes plus the
 * stage moves, so they can track the deal from their side.
 *
 * The two note streams never cross — staff never read a buyer's notes and a
 * buyer never reads the desk's, matching the endpoint.
 */
export default function DealNotesDrawer({ card: incoming, isStaff, buyerCandidates, onClose, onCardChanged, onBoardChanged }: Props) {
  const toast = useToast();
  // The drawer's own copy: a write on a virtual deal gives it a card_id, and
  // every later call has to carry that id.
  const [card, setCard] = useState<DealCard | null>(incoming);
  const [notes, setNotes] = useState<DealNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [details, setDetails] = useState<DetailsDraft | null>(null);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [linkBuyer, setLinkBuyer] = useState("");

  const open = incoming !== null;
  const openKey = incoming ? `${incoming.buyer?.id ?? ""}:${incoming.listing.listing_id}:${incoming.card_id ?? ""}` : "";

  useEffect(() => {
    setCard(incoming);
    setDetails(incoming ? draftFrom(incoming) : null);
    setDraft("");
    setEditing(null);
    setLinkBuyer("");
    // Re-seed only when a different deal is opened, not on every board refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const load = useCallback(async (target: DealCard) => {
    setLoading(true);
    setLoadFailed(false);
    try {
      setNotes(await api.post<DealNote[]>("/deal-pipeline/notes/history", dealTarget(target)));
    } catch {
      // A deal with nothing written yet comes back as [], so reaching here means
      // the read genuinely failed. Say so — rendering it as "No notes yet" tells
      // whoever opens the deal that nothing was ever written.
      setNotes([]);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (incoming) load(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onClose]);

  /** Run a write, then fold the returned card back in. */
  const run = async <T,>(fn: () => Promise<T>, fail: string): Promise<T | null> => {
    if (busy) return null;
    setBusy(true);
    try {
      return await fn();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : fail);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const applyCard = (updated: DealCard, wasVirtual: boolean) => {
    setCard(updated);
    if (wasVirtual) onBoardChanged();
    else onCardChanged(updated);
  };

  if (!card || !details) return null;

  const isListingOnly = card.source === "listing";
  const closed = card.outcome !== null;

  const submitNote = async () => {
    const body = draft.trim();
    if (!body) return;
    const wasVirtual = card.card_id === null;
    const note = await run(() => api.post<DealNote>("/deal-pipeline/notes", { ...dealTarget(card), body }), "Failed to save the note");
    if (!note) return;
    setNotes((n) => [note, ...n]);
    setDraft("");
    // The first note on a virtual deal creates its row — reload so the board
    // (and this drawer) pick up the new id.
    if (wasVirtual) onBoardChanged();
    else onCardChanged({ ...card, note_count: card.note_count + 1 });
  };

  const saveEdit = async () => {
    if (!editing || !editing.body.trim()) return;
    const note = await run(() => api.patch<DealNote>(`/deal-pipeline/notes/${editing.id}`, { body: editing.body.trim() }), "Failed to save the note");
    if (!note) return;
    setNotes((ns) => ns.map((n) => (n.id === note.id ? note : n)));
    setEditing(null);
  };

  const removeNote = async (n: DealNote) => {
    if (!confirm("Delete this note? This cannot be undone.")) return;
    const ok = await run(() => api.delete(`/deal-pipeline/notes/${n.id}`), "Failed to delete the note");
    if (ok === null) return;
    setNotes((ns) => ns.filter((x) => x.id !== n.id));
    onCardChanged({ ...card, note_count: Math.max(0, card.note_count - 1) });
  };

  const saveDetails = async () => {
    const offer = details.offer_amount.trim();
    if (offer && (Number.isNaN(Number(offer)) || Number(offer) < 0)) {
      toast.error("Offer must be a positive number");
      return;
    }
    const wasVirtual = card.card_id === null;
    const updated = await run(() => api.patch<DealCard>("/deal-pipeline/cards/details", {
      ...dealTarget(card),
      offer_amount: offer ? Number(offer) : null,
      target_close_date: details.target_close_date || null,
      next_step: details.next_step.trim() || null,
      next_step_due: details.next_step_due || null,
    }), "Failed to save the deal details");
    if (!updated) return;
    applyCard(updated, wasVirtual);
    setDetails(draftFrom(updated));
    toast.success("Deal details saved");
  };

  const setOutcome = async (outcome: "won" | "lost" | null) => {
    if (outcome === "lost" && !confirm("Close this deal out as lost? It leaves the board; you can reopen it from “Show closed”.")) return;
    const wasVirtual = card.card_id === null;
    const updated = await run(() => api.put<DealCard>("/deal-pipeline/cards/outcome", { ...dealTarget(card), outcome }), "Failed to update the deal");
    if (!updated) return;
    applyCard(updated, wasVirtual);
    load(updated);
  };

  const assignBuyer = async () => {
    if (!linkBuyer || !card.card_id) return;
    const updated = await run(() => api.post<DealCard>(`/deal-pipeline/cards/${card.card_id}/assign-buyer`, { buyer_id: linkBuyer }), "Failed to link the buyer");
    if (!updated) return;
    toast.success("Buyer linked to the deal");
    setCard(updated);
    setLinkBuyer("");
    onBoardChanged();
    load(updated);
  };

  const removeDeal = async () => {
    if (!card.card_id || !confirm("Remove this deal from the board? Its notes are deleted with it.")) return;
    const ok = await run(() => api.delete(`/deal-pipeline/cards/${card.card_id}`), "Failed to remove the deal");
    if (ok === null) return;
    onBoardChanged();
    onClose();
  };

  const detailsDirty = JSON.stringify(details) !== JSON.stringify(draftFrom(card));

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] bg-black/50 backdrop-blur-[2px]"
        onClick={() => !busy && onClose()}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Deal"
        className="fixed inset-y-0 right-0 z-[101] flex w-full max-w-md flex-col border-l border-border bg-background shadow-2xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border/80 bg-card px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <NotebookPen className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-bold tracking-tight text-foreground">
                {card.listing.title || "Untitled listing"}
              </h2>
              <p className="line-clamp-1 text-xs text-muted-foreground">
                {card.listing.reference_code && <span className="font-mono">{card.listing.reference_code} · </span>}
                {stageLabel(card.stage)}
                {isStaff && (card.buyer ? ` · ${card.buyer.first_name} ${card.buyer.last_name}` : " · No buyer yet")}
                {card.advisor && ` · Advisor: ${card.advisor.name}`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            aria-label="Close"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {card.withdrawn && (
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs leading-5 text-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              The buyer withdrew their interest. The deal can no longer be moved — close it out as lost, or leave it until they re-engage.
            </div>
          )}

          {isStaff && (
            <section className="mb-5 rounded-xl border border-border/70 bg-card p-4">
              <h3 className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Deal details</h3>
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 text-xs text-muted-foreground sm:col-span-1">
                  Offer amount ($)
                  <Input
                    inputMode="decimal"
                    value={details.offer_amount}
                    onChange={(e) => setDetails({ ...details, offer_amount: e.target.value.replace(/[^0-9.]/g, "") })}
                    placeholder={card.listing.asking_price != null ? `Asking ${fmtExact(card.listing.asking_price)}` : "—"}
                    className="mt-1"
                  />
                </label>
                <label className="col-span-2 text-xs text-muted-foreground sm:col-span-1">
                  Target close
                  <Input
                    type="date"
                    value={details.target_close_date}
                    onChange={(e) => setDetails({ ...details, target_close_date: e.target.value })}
                    className="mt-1"
                  />
                </label>
                <label className="col-span-2 text-xs text-muted-foreground">
                  Next step
                  <Input
                    value={details.next_step}
                    maxLength={500}
                    onChange={(e) => setDetails({ ...details, next_step: e.target.value })}
                    placeholder="e.g. Send CIM, book seller call"
                    className="mt-1"
                  />
                </label>
                <label className="col-span-2 text-xs text-muted-foreground sm:col-span-1">
                  Next step due
                  <Input
                    type="date"
                    value={details.next_step_due}
                    onChange={(e) => setDetails({ ...details, next_step_due: e.target.value })}
                    className="mt-1"
                  />
                </label>
                <div className="col-span-2 flex items-end justify-end sm:col-span-1">
                  <Button size="sm" onClick={saveDetails} disabled={!detailsDirty || busy}>
                    Save details
                  </Button>
                </div>
              </div>
            </section>
          )}

          {isStaff && (
            <section className="mb-5 rounded-xl border border-border/70 bg-card p-4">
              <h3 className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">Outcome</h3>
              {closed ? (
                <div className="flex items-center justify-between gap-3">
                  <p className={cn("text-sm font-semibold", card.outcome === "won" ? "text-emerald-500" : "text-muted-foreground")}>
                    {OUTCOME_TEXT[card.outcome!]}
                  </p>
                  <Button size="sm" variant="outline" onClick={() => setOutcome(null)} disabled={busy}>
                    <RotateCcw className="h-3.5 w-3.5" /> Reopen
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => setOutcome("won")} disabled={busy || card.withdrawn}>
                    <Trophy className="h-3.5 w-3.5" /> Mark won
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setOutcome("lost")} disabled={busy}>
                    <CircleSlash className="h-3.5 w-3.5" /> Mark lost
                  </Button>
                  {isListingOnly && (
                    <Button size="sm" variant="ghost" onClick={removeDeal} disabled={busy} className="ml-auto text-muted-foreground">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </Button>
                  )}
                </div>
              )}
              {isListingOnly && !closed && (
                <div className="mt-4 border-t border-border/60 pt-4">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-foreground">
                    <Link2 className="h-3.5 w-3.5 text-primary" /> Link a buyer
                  </p>
                  {buyerCandidates.length === 0 ? (
                    <p className="text-xs text-muted-foreground">
                      No buyer has expressed interest in this listing yet. Once one does, link them here and their deal picks up this card's stage and notes.
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <SearchableSelect
                        value={linkBuyer}
                        onChange={setLinkBuyer}
                        placeholder="Choose an interested buyer…"
                        options={buyerCandidates.map((b) => ({ value: b.id, label: `${b.first_name} ${b.last_name} · ${b.email}` }))}
                        className="min-w-0 flex-1"
                      />
                      <Button size="sm" onClick={assignBuyer} disabled={!linkBuyer || busy}>Link</Button>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          <h3 className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
            {isStaff ? "Notes & history" : "My notes & progress"}
          </h3>
          {loading ? (
            <Loader className="min-h-[20vh]" />
          ) : loadFailed ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-amber-500/15">
                <AlertTriangle className="h-7 w-7 text-amber-600" />
              </div>
              <p className="mt-4 font-semibold text-foreground">Could not load the notes</p>
              <p className="mt-1 max-w-[17rem] text-sm text-muted-foreground">
                Anything already written on this deal is still saved — this is a
                problem reading it, not a blank history.
              </p>
              <Button size="sm" variant="outline" onClick={() => load(card)} className="mt-4">
                Try again
              </Button>
            </div>
          ) : notes.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-muted/60">
                <MessageSquareText className="h-7 w-7 text-muted-foreground/40" />
              </div>
              <p className="mt-4 font-semibold text-foreground">No notes yet</p>
              <p className="mt-1 max-w-[16rem] text-sm text-muted-foreground">
                {isStaff
                  ? "Jot down what happened — calls, blockers, next steps — so the next person picking this up is not starting cold."
                  : "Keep your own notes on this deal — questions for the seller, numbers to check, what you liked."}
              </p>
            </div>
          ) : (
            <ol className="flex flex-col gap-3">
              {notes.map((n) => (
                <li key={n.id} className="rounded-xl border border-border/70 bg-card p-3.5 shadow-sm">
                  {n.kind === "stage_change" ? (
                    <p className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
                      <span className="text-muted-foreground">{stageLabel(n.from_stage)}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-primary" />
                      <span className="font-bold text-primary">{stageLabel(n.to_stage)}</span>
                    </p>
                  ) : n.kind === "outcome" ? (
                    <p className="flex items-center gap-1.5 text-sm font-bold text-foreground">
                      {n.body === "won" ? <Trophy className="h-3.5 w-3.5 text-emerald-500" /> : n.body === "lost" ? <CircleSlash className="h-3.5 w-3.5 text-muted-foreground" /> : <RotateCcw className="h-3.5 w-3.5 text-primary" />}
                      {OUTCOME_TEXT[n.body ?? ""] ?? n.body}
                    </p>
                  ) : editing?.id === n.id ? (
                    <div>
                      <Textarea
                        value={editing.body}
                        onChange={(e) => setEditing({ id: n.id, body: e.target.value })}
                        rows={3}
                        maxLength={4000}
                        className="resize-none"
                      />
                      <div className="mt-2 flex justify-end gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
                        <Button size="sm" onClick={saveEdit} disabled={!editing.body.trim() || busy}>
                          <Check className="h-3.5 w-3.5" /> Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-sm leading-6 text-foreground">{n.body}</p>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <p className="text-[0.68rem] text-muted-foreground">
                      {n.author_name || "Someone"} · {formatWhen(n.created_at)}
                      {n.edited_at && " · edited"}
                    </p>
                    {n.kind === "note" && editing?.id !== n.id && (n.is_mine || isStaff) && (
                      <div className="flex items-center gap-1">
                        {n.is_mine && (
                          <button
                            type="button"
                            title="Edit note"
                            onClick={() => setEditing({ id: n.id, body: n.body ?? "" })}
                            className="rounded-md p-1 text-muted-foreground/70 hover:bg-muted hover:text-primary"
                          >
                            <Pencil className="h-3 w-3" />
                          </button>
                        )}
                        {/* The server lets an author delete their own note, and a
                            platform admin delete any staff note; a refusal comes
                            back as a toast. */}
                        <button
                          type="button"
                          title="Delete note"
                          onClick={() => removeNote(n)}
                          className="rounded-md p-1 text-muted-foreground/70 hover:bg-muted hover:text-red-500"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="shrink-0 border-t border-border/80 bg-card px-5 py-4">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder={isStaff ? "What happened on this deal?" : "A private note on this deal…"}
            className="resize-none"
          />
          <div className="mt-2.5 flex items-center justify-between gap-3">
            <span className="text-[0.68rem] text-muted-foreground">
              {isStaff ? "Internal — the buyer never sees these." : "Private — only you can see your notes."}
            </span>
            <Button size="sm" onClick={submitNote} disabled={!draft.trim() || busy}>
              {busy ? "Saving…" : "Add note"}
            </Button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}
